import {
  List,
  Datagrid,
  ReferenceField,
  NumberField,
  BooleanField,
  TextField,
  ReferenceInput,
  BooleanInput,
  TextInput,
  Edit,
  SimpleForm,
  Show,
  SimpleShowLayout,
  useRecordContext,
  useGetOne,
} from 'react-admin'

// <Edit title={<PostTitle />}>
// const PostTitle = () => {
//   const record = useRecordContext()
//   return <span>Post {record ? `'${record.title}'` : ''}</span>
// }

const FormattedAmountField = ({ source }) => {
  const record = useRecordContext()
  const assetTokenId = record?.assetTokenId
  const amount = record?.[source]

  const { data: token, isLoading } = useGetOne(
    'tokens',
    { id: assetTokenId },
    { enabled: !!assetTokenId }
  )

  if (isLoading || !token || amount === undefined || amount === null) {
    return <span>-</span>
  }

  const decimals = token.decimals || 0
  const amountStr = String(Math.floor(Number(amount))) // Ensure integer string
  const divisor = Math.pow(10, decimals)

  // Handle division using string manipulation for precision with large numbers
  // Manual division algorithm for large integers
  const divideLargeNumber = (dividendStr, divisorNum) => {
    const dividend = dividendStr.split('').map(Number)
    let quotient = []
    let remainder = 0

    for (let i = 0; i < dividend.length; i++) {
      remainder = remainder * 10 + dividend[i]
      quotient.push(Math.floor(remainder / divisorNum))
      remainder = remainder % divisorNum
    }

    // Remove leading zeros from quotient
    while (quotient.length > 1 && quotient[0] === 0) {
      quotient.shift()
    }

    return {
      quotient: quotient.join(''),
      remainder: remainder,
    }
  }

  const result = divideLargeNumber(amountStr, divisor)
  const quotientStr = result.quotient || '0'
  const remainderNum = result.remainder

  // Format the decimal part
  const remainderStr = String(remainderNum).padStart(decimals, '0')

  // Combine quotient and remainder
  let valueStr = quotientStr
  if (remainderStr !== '0'.repeat(decimals)) {
    // Remove trailing zeros from remainder
    const trimmedRemainder = remainderStr.replace(/0+$/, '')
    if (trimmedRemainder) {
      valueStr = `${quotientStr}.${trimmedRemainder}`
    }
  }

  const decimalIndex = valueStr.indexOf('.')

  if (decimalIndex === -1) {
    // No decimal point, just return the integer
    return <span>{valueStr}</span>
  }

  const integerPart = valueStr.substring(0, decimalIndex)
  const decimalPart = valueStr.substring(decimalIndex + 1)

  // Show only 6 digits after decimal, add ... if there are more
  const truncatedDecimal = decimalPart.length > 6 ? decimalPart.substring(0, 6) + '...' : decimalPart

  return (
    <span>
      {integerPart}.{truncatedDecimal}
    </span>
  )
}

export const UserDepositList = ({ isEdit, isDelete }) => (
  <List
    filters={[
      <ReferenceInput source="campaignId" reference="campaigns" />,
      <ReferenceInput source="networkIdFrom" reference="networkFroms" />,
      <ReferenceInput source="networkIdTo" reference="networkTos" />,
      <ReferenceInput source="assetTokenId" reference="assetTokens" />,
      <ReferenceInput source="protocolTokenId" reference="protocolTokens" />,
      <BooleanInput source="isBonusProcessed" />,
      <BooleanInput source="isBonusRequested" />,
      <TextInput source="walletAddress" />,
    ]}
  >
    <Datagrid bulkActionButtons={false} rowClick={isEdit ? 'edit' : 'show'}>
      <ReferenceField source="campaignId" reference="campaigns" />
      <ReferenceField source="userId" reference="users" />
      <ReferenceField source="userId" reference="users">
        <TextField source="walletAddress" />
      </ReferenceField>,
      <ReferenceField source="networkIdFrom" reference="networks" />
      <ReferenceField source="networkIdTo" reference="networks" />
      <ReferenceField source="assetTokenId" reference="tokens" />
      <FormattedAmountField source="amount" />
      <ReferenceField source="rewardTokenId" reference="tokens" />
      <NumberField source="rewardAmount" />
      <ReferenceField source="protocolTokenId" reference="tokens" />
      <NumberField source="protocolAmount" />
      <BooleanField source="isBonusProcessed" />
      <BooleanField source="isBonusRequested" />
      <TextField source="transactionHash" />
      <TextField source="metadata" />
      <NumberField source="points" />
      <TextField source="walletAddress" />
    </Datagrid>
  </List>
)

export const UserDepositEdit = () => (
  <Edit>
    <SimpleForm>
      <BooleanInput source="isBonusProcessed" />
      <BooleanInput source="isBonusRequested" />
    </SimpleForm>
  </Edit>
)

export const UserDepositShow = () => (
  <Show>
    <SimpleShowLayout>
      <ReferenceField source="campaignId" reference="campaigns" />
      <ReferenceField source="userId" reference="users" />
      <ReferenceField source="campaignContractId" reference="campaignContracts" />
      <ReferenceField source="networkIdFrom" reference="networks" />
      <ReferenceField source="networkIdTo" reference="networks" />
      <ReferenceField source="assetTokenId" reference="tokens" />
      <FormattedAmountField source="amount" />
      <NumberField source="amount" label="Amount (raw)" />
      <ReferenceField source="rewardTokenId" reference="tokens" />
      <NumberField source="rewardAmount" />
      <ReferenceField source="protocolTokenId" reference="tokens" />
      <NumberField source="protocolAmount" />
      <BooleanField source="isBonusProcessed" />
      <BooleanField source="isBonusRequested" />
      <TextField source="transactionHash" />
      <TextField source="metadata" />
      <NumberField source="points" />
      <TextField source="walletAddress" />
    </SimpleShowLayout>
  </Show>
)
